import { useSyncExternalStore } from 'react'

/**
 * How much of the layout viewport's bottom an on-screen keyboard covers, in px
 * (agent-1.1 S9.2, K15).
 *
 * The idea is AIVY's `computeShellHeight` (aivy-coach frontend/src/lib/nativeShell.ts
 * @ 11af75af): the visual viewport is what is left visible; whatever lies below its
 * bottom edge is covered. AIVY needs a native shell to learn the keyboard height;
 * a plain browser exposes it through `window.visualViewport`, whose height shrinks
 * (and `offsetTop` moves, when the page is panned) while the keyboard is up.
 */
export function keyboardInset(
  innerHeight: number,
  viewport: { height: number; offsetTop: number } | null | undefined,
): number {
  if (!viewport) return 0
  return Math.max(0, Math.round(innerHeight - viewport.height - viewport.offsetTop))
}

function subscribe(onChange: () => void): () => void {
  const viewport = typeof window === 'undefined' ? null : window.visualViewport
  if (!viewport) return () => undefined
  viewport.addEventListener('resize', onChange)
  viewport.addEventListener('scroll', onChange)
  return () => {
    viewport.removeEventListener('resize', onChange)
    viewport.removeEventListener('scroll', onChange)
  }
}

function snapshot(): number {
  return typeof window === 'undefined' ? 0 : keyboardInset(window.innerHeight, window.visualViewport)
}

/** The live keyboard inset; 0 where there is no visual viewport (and on the server). */
export function useKeyboardInset(): number {
  return useSyncExternalStore(subscribe, snapshot, () => 0)
}
